import { Box, type SxProps } from '@mui/material';
import React from 'react';
import { DropzoneState, useDropzone } from 'react-dropzone';

import { AxleTheme } from '../../theme';
import { ReturnComponent } from '../../utils/reactTypes';

const getDropzoneBorderColor = ({
    isDragAccept,
    isDragReject,
    isDragActive,
}: DropzoneState): string => {
    if (isDragAccept) return '#00e676';
    if (isDragReject) return '#ff1744';
    if (isDragActive) return '#2196f3';
    return '#eeeeee';
};

const AcceptedFilesList = ({
    dropzoneState,
}: {
    dropzoneState: DropzoneState;
}): ReturnComponent => (
    <ul style={{ paddingLeft: 0 }}>
        {dropzoneState.acceptedFiles.map((file, index) => (
            <li
                style={{ textAlign: 'left', listStyleType: 'none', marginBottom: '4px' }}
                key={index}
            >
                {file.name}
            </li>
        ))}
    </ul>
);

export type AxleDropzoneProps = {
    onUpload: (files: File[]) => void;
    sx?: SxProps<typeof AxleTheme>;
    showFilesList?: boolean;
};

export function AxleDropzone({
    onUpload,
    sx,
    showFilesList = false,
}: AxleDropzoneProps): ReturnComponent {
    const dropzoneState = useDropzone({
        onDropAccepted: onUpload,
        multiple: false,
    });
    const hasAcceptedFiles = dropzoneState.acceptedFiles.length > 0;
    return (
        <section>
            <Box
                {...dropzoneState.getRootProps()}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderWidth: '2px',
                    borderRadius: '2px',
                    borderColor: getDropzoneBorderColor(dropzoneState),
                    borderStyle: 'dashed',
                    backgroundColor: 'white',
                    color: '#999',
                    outline: 'none',
                    transition: 'border .24s ease-in-out',
                    cursor: 'pointer',
                    ...sx,
                }}
            >
                <input {...dropzoneState.getInputProps()} />
                {showFilesList && hasAcceptedFiles ? (
                    <AcceptedFilesList dropzoneState={dropzoneState} />
                ) : (
                    <p>Drag & drop files here, or click to select files</p>
                )}
            </Box>
        </section>
    );
}
